import React from 'react';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import LiveTvIcon from '@mui/icons-material/LiveTv';
import WebIcon from '@mui/icons-material/Web';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

const plans = [
  {
    name: 'Streaming',
    tagline: 'Broadcast at scale, delay-free',
    icon: <LiveTvIcon sx={{ fontSize: 36 }} />,
    color: '#e53935',
    bgColor: 'rgba(229, 57, 53, 0.08)',
    borderColor: 'rgba(229, 57, 53, 0.2)',
    featured: true,
    bullets: [
      'Sub-second latency RTMP & SRT ingest on optimized edge servers',
      '99.9% uptime SLA with real-time stream health monitoring',
      'Custom ingest keys, playback URLs & domain whitelisting',
      'Support with instant failover & restreaming',
    ],
  },
  {
    name: 'Web App Development',
    tagline: 'Mobile and SEO friendly',
    icon: <WebIcon sx={{ fontSize: 36 }} />,
    color: '#388e3c',
    bgColor: 'rgba(56, 142, 60, 0.08)',
    borderColor: 'rgba(56, 142, 60, 0.2)',
    bullets: [
      'Custom admin dashboards & stream management control panels',
      'Secure auth, role-based access & third-party API integrations',
      'Performance-tuned for Core Web Vitals & mobile-first UX',
      'Ongoing maintenance, hosting guidance & feature rollouts',
    ],
  },
  {
    name: 'Mobile App Development',
    tagline: 'Your app, in every pocket',
    icon: <PhoneAndroidIcon sx={{ fontSize: 36 }} />,
    color: '#7b1fa2',
    bgColor: 'rgba(123, 31, 162, 0.08)',
    borderColor: 'rgba(123, 31, 162, 0.2)',
    bullets: [
      'Android apps published to Play Store',
      'Live playback, push alerts & offline viewing for your audience',
      'Branded UI/UX tailored to your channel identity & user flow',
      'End-to-end deployment, store compliance & version updates',
    ],
  },
];

const PricingCard = ({ plan, onContactClick }) => (
  <Box
    sx={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      borderRadius: '20px',
      border: plan.featured ? `2px solid ${plan.color}` : `1px solid ${plan.borderColor}`,
      background: '#ffffff',
      boxShadow: plan.featured
        ? `0 20px 56px ${plan.bgColor.replace('0.08', '0.35')}`
        : '0 8px 24px rgba(5, 15, 102, 0.08)',
      transition: 'all 0.3s ease',
      overflow: 'hidden',
      position: 'relative',
      '&:hover': {
        transform: 'translateY(-8px)',
        boxShadow: `0 24px 64px ${plan.bgColor.replace('0.08', '0.3')}`,
      },
    }}
  >
    {plan.featured && (
      <Box
        sx={{
          background: plan.color,
          color: 'white',
          textAlign: 'center',
          py: 0.75,
          fontSize: '12px',
          fontWeight: 700,
          letterSpacing: '1px',
          textTransform: 'uppercase',
        }}
      >
        Most Popular
      </Box>
    )}
    <Box sx={{ p: { xs: 3, md: 3.5 }, flex: 1, display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          width: 72,
          height: 72,
          borderRadius: '18px',
          background: plan.bgColor,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: plan.color,
          mb: 2.5,
        }}
      >
        {plan.icon}
      </Box>
      <Typography
        component="h3"
        sx={{ fontWeight: 800, fontSize: '1.25rem', color: '#0d1757', mb: 0.5 }}
      >
        {plan.name}
      </Typography>
      <Typography sx={{ color: '#6b7280', fontSize: '0.9rem', mb: 3, lineHeight: 1.5 }}>
        {plan.tagline}
      </Typography>

      <Stack spacing={1.5} sx={{ mb: 3, flex: 1 }}>
        {plan.bullets.map((bullet) => (
          <Stack key={bullet} direction="row" spacing={1.2} alignItems="flex-start">
            <CheckCircleOutlineIcon
              sx={{ fontSize: 18, color: plan.color, mt: 0.25, flexShrink: 0 }}
            />
            <Typography sx={{ color: '#374151', fontSize: '0.88rem', lineHeight: 1.6 }}>
              {bullet}
            </Typography>
          </Stack>
        ))}
      </Stack>

      <Button
        fullWidth
        variant={plan.featured ? 'contained' : 'outlined'}
        onClick={onContactClick}
        sx={{
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '15px',
          borderRadius: '10px',
          py: 1.3,
          ...(plan.featured
            ? {
                background: '#050f66',
                boxShadow: 'none',
                '&:hover': { background: '#0a1882', boxShadow: 'none' },
              }
            : {
                color: '#050f66',
                borderColor: 'rgba(5, 15, 102, 0.35)',
                '&:hover': {
                  background: 'rgba(5, 15, 102, 0.04)',
                  borderColor: '#050f66',
                },
              }),
        }}
      >
        Get a Quote
      </Button>
    </Box>
  </Box>
);

const Pricing = ({ onContactClick }) => (
  <Box
    id="pricing"
    sx={{
      background: '#f8f9ff',
      py: { xs: 6, md: 10 },
    }}
  >
    <Container maxWidth="lg">
      <Box sx={{ textAlign: 'center', mb: { xs: 5, md: 7 } }}>
        <Typography
          component="span"
          sx={{
            display: 'inline-block',
            background: 'rgba(5, 15, 102, 0.07)',
            color: '#050f66',
            fontWeight: 700,
            fontSize: '13px',
            letterSpacing: '1.5px',
            textTransform: 'uppercase',
            px: 2,
            py: 0.8,
            borderRadius: '20px',
            mb: 2,
          }}
        >
          Pricing
        </Typography>
        <Typography
          component="h2"
          sx={{
            color: '#0d1757',
            fontSize: { xs: '2rem', md: '2.8rem' },
            fontWeight: 800,
            mb: 2,
            lineHeight: 1.15,
          }}
        >
          Plans Tailored to You
        </Typography>
        <Typography
          sx={{
            color: '#6b7280',
            fontSize: { xs: '1rem', md: '1.05rem' },
            maxWidth: '560px',
            mx: 'auto',
            lineHeight: 1.7,
          }}
        >
          Every package is fully customized — pick a starting point and we&apos;ll shape it around
          your goals, scale, and budget.
        </Typography>
      </Box>

      <Grid container spacing={3} alignItems="stretch">
        {plans.map((plan) => (
          <Grid item xs={12} md={4} key={plan.name}>
            <PricingCard plan={plan} onContactClick={onContactClick} />
          </Grid>
        ))}
      </Grid>
    </Container>
  </Box>
);

export default Pricing;
